import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import {
  AdminTaskDetail,
  AdminTaskRow,
  adminTaskApi,
  UpdateTaskPayload,
} from '../../../../api/adminTaskApi';
import { adminCatalogApi, CatalogRow } from '../../../../api/adminCatalogApi';
import { paths } from '../../../../routes/paths';
import { colors, primaryButtonSx, secondaryButtonSx, typo } from '../../../../theme/tokens';
import { WriteButton } from '../../../../components/ui';

import TaskVersionHistoryDrawer from './TaskVersionHistoryDrawer';
import PublishConfirmationDialog, {
  PublishEntity,
} from './PublishConfirmationDialog';
import RollbackDialog from './RollbackDialog';

/* ------------------------------------------------------------------ */
/*  Local types                                                        */
/* ------------------------------------------------------------------ */

interface FormState {
  task_name: string;
  task_type_code: string;
  task_category_code: string;
  self_report: boolean;
  confirm_report: boolean;
  is_subtask: boolean;
  active: boolean;
}

function formFromDetail(d: AdminTaskDetail): FormState {
  return {
    task_name: d.task_name ?? '',
    task_type_code: d.task_type_code ?? '',
    task_category_code: d.task_category_code ?? '',
    self_report: !!d.self_report,
    confirm_report: !!d.confirm_report,
    is_subtask: !!d.is_subtask,
    active: !!d.active,
  };
}

function statusChipProps(status: AdminTaskRow['status']): { label: string; bg: string; color: string } {
  if (status === 'unpublished')
    return { label: 'Unpublished', bg: colors.warningBgStrong, color: colors.warningText };
  if (status === 'in_sync')
    return { label: 'In sync', bg: colors.successBgStrong, color: colors.successText };
  return { label: 'Never published', bg: colors.bgMuted, color: colors.textSecondary };
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/* ------------------------------------------------------------------ */
/*  Section label helper                                               */
/* ------------------------------------------------------------------ */

const SectionLabel: React.FC<{ label: string; hint?: string }> = ({ label, hint }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 700,
        fontFamily: 'Manrope, sans-serif',
        color: colors.adminAccent,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {label}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: 12, color: colors.textTertiary, mt: 0.25 }}>{hint}</Typography>
    )}
  </Box>
);

/* ------------------------------------------------------------------ */
/*  Main                                                               */
/* ------------------------------------------------------------------ */

const TaskEdit: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versionConflict, setVersionConflict] = useState<number | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });
  const initialFormRef = useRef<FormState | null>(null);

  const detailQuery = useQuery<AdminTaskDetail>({
    queryKey: ['admin', 'tasks', code],
    queryFn: () => adminTaskApi.get(code),
    enabled: !!code,
  });

  const { data: types = [] } = useQuery<CatalogRow[]>({
    queryKey: ['admin', 'catalogs', 'task-types'],
    queryFn: () => adminCatalogApi.list('task-types'),
  });
  const { data: cats = [] } = useQuery<CatalogRow[]>({
    queryKey: ['admin', 'catalogs', 'task-categories'],
    queryFn: () => adminCatalogApi.list('task-categories'),
  });

  // Sync local form from fetched detail whenever it arrives / changes.
  useEffect(() => {
    if (!detailQuery.data) return;
    const next = formFromDetail(detailQuery.data);
    setForm(next);
    initialFormRef.current = next;
    setBaseVersion(detailQuery.data.current_version);
  }, [detailQuery.data]);

  const isDirty = useMemo(() => {
    if (!form || !initialFormRef.current) return false;
    const a = form;
    const b = initialFormRef.current;
    return (
      a.task_name !== b.task_name ||
      a.task_type_code !== b.task_type_code ||
      a.task_category_code !== b.task_category_code ||
      a.self_report !== b.self_report ||
      a.confirm_report !== b.confirm_report ||
      a.is_subtask !== b.is_subtask ||
      a.active !== b.active
    );
  }, [form]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!form || baseVersion == null) throw new Error('No form state');
      const payload: UpdateTaskPayload = {
        base_version: baseVersion,
        task_name: form.task_name,
        task_type_code: form.task_type_code,
        task_category_code: form.task_category_code,
        self_report: form.self_report,
        confirm_report: form.confirm_report,
        is_subtask: form.is_subtask,
      };
      return adminTaskApi.update(code, payload);
    },
    onSuccess: (row) => {
      setVersionConflict(null);
      setEditMode(false);
      setSnackbar({ open: true, msg: `Saved v${row.current_version}`, sev: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', code] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'list'] });
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.error === 'version_conflict') {
        setVersionConflict(data.current_version);
      } else {
        setSnackbar({ open: true, msg: 'Save failed — try again.', sev: 'error' });
      }
    },
  });

  // Reactivation is a small dedicated mutation (vs. reusing saveMutation)
  // so it shows its own pending state + copy in the confirm dialog
  // without interfering with a possibly-dirty form on screen.
  const reactivateMutation = useMutation({
    mutationFn: async () => {
      if (baseVersion == null) throw new Error('No base version');
      return adminTaskApi.update(code, {
        base_version: baseVersion,
        active: true,
      });
    },
    onSuccess: (row) => {
      setReactivateOpen(false);
      setSnackbar({ open: true, msg: `Reactivated ${row.task_code}`, sev: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', code] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'list'] });
    },
    onError: () => {
      setSnackbar({ open: true, msg: 'Reactivate failed — try again.', sev: 'error' });
    },
  });

  const handleCancelEdit = useCallback(() => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) return;
    if (initialFormRef.current) setForm(initialFormRef.current);
    setEditMode(false);
    setVersionConflict(null);
  }, [isDirty]);

  const handleReload = useCallback(async () => {
    if (isDirty && !window.confirm('Discard local changes and reload the latest version?')) return;
    setVersionConflict(null);
    await queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', code] });
    setEditMode(false);
  }, [isDirty, queryClient, code]);

  // Keyboard shortcuts: e = edit, v = history, Cmd/Ctrl+S = save
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const tag = (ev.target as HTMLElement | null)?.tagName;
      const isInputTarget = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      const cmd = ev.metaKey || ev.ctrlKey;

      if (cmd && ev.key.toLowerCase() === 's') {
        ev.preventDefault();
        if (editMode && isDirty && !saveMutation.isPending) saveMutation.mutate();
        return;
      }
      if (!isInputTarget) {
        if (ev.key === 'e' && !editMode) {
          ev.preventDefault();
          setEditMode(true);
        } else if (ev.key === 'v') {
          ev.preventDefault();
          setHistoryOpen(true);
        } else if (ev.key === 'Escape' && editMode) {
          handleCancelEdit();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode, isDirty, saveMutation, handleCancelEdit]);

  /* ---------- Render paths ---------- */

  if (detailQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.adminAccent }} />
      </Box>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            size="small"
            onClick={() => navigate(paths.admin.globalCatalog.tasks())}
            sx={{ color: colors.errorText }}
          >
            Back to list
          </Button>
        }
      >
        Couldn&apos;t load task <code>{code}</code>.
      </Alert>
    );
  }

  const detail = detailQuery.data;
  const cp = statusChipProps(detail.status);
  // platform_version isn't wired on the backend yet (Round 3). Treat it as
  // current_version when present platform_task_id is null, so the amber
  // "unpublished" state doesn't fire spuriously.
  const platformVersion = detail.platform_task_id == null ? null : detail.current_version;
  const hasUnpublished = platformVersion != null && detail.current_version > platformVersion;
  // Inactive = form fields locked, top-right Edit hidden, save bar
  // hidden. Only the Reactivate path (via the info banner) can mutate.
  const isInactive = !detail.active;

  return (
    <Box sx={{ maxWidth: 1200, pb: editMode ? 10 : 4 }}>
      {/* Breadcrumb */}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: colors.textSecondary,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          mb: 1.5,
        }}
      >
        <MuiLink
          onClick={() => navigate(paths.admin.home())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mr: 0.5 }}
        >
          Admin
        </MuiLink>
        /
        <MuiLink
          onClick={() => navigate(paths.admin.globalCatalog.root())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mx: 0.5 }}
        >
          Global Catalog
        </MuiLink>
        /
        <MuiLink
          onClick={() => navigate(paths.admin.globalCatalog.tasks())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mx: 0.5 }}
        >
          Tasks
        </MuiLink>
        /
        <span style={{ marginLeft: 4, fontFamily: 'Fira Code, monospace', color: colors.textPrimary }}>
          {detail.task_code}
        </span>
      </Typography>

      {/* Title row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 1.5,
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{ ...typo.pageTitle, fontSize: 32, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {detail.task_name || detail.task_code}
          </Typography>
          {detail.task_type_code && (
            <Chip
              label={detail.task_type_code}
              size="small"
              sx={{
                fontFamily: 'Fira Code, monospace',
                fontSize: 12,
                backgroundColor: colors.adminAccentSubtle,
                color: colors.adminAccent,
                fontWeight: 600,
              }}
            />
          )}
          <Chip
            label={cp.label}
            size="small"
            sx={{
              fontSize: 12,
              fontWeight: 700,
              backgroundColor: cp.bg,
              color: cp.color,
              borderRadius: '6px',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setHistoryOpen(true)}
            sx={secondaryButtonSx}
          >
            History <Box component="span" sx={{ fontSize: 11, opacity: 0.6, ml: 0.5 }}>(v)</Box>
          </Button>
          {!editMode && !isInactive && (
            <WriteButton
              size="small"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditMode(true)}
              sx={primaryButtonSx}
            >
              Edit <Box component="span" sx={{ fontSize: 11, opacity: 0.7, ml: 0.5 }}>(e)</Box>
            </WriteButton>
          )}
        </Box>
      </Box>

      {/* Metadata sub-line */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          mb: 3,
          ...typo.smallText,
          color: hasUnpublished ? colors.warningText : colors.textSecondary,
        }}
      >
        <span>Current version: v{detail.current_version}</span>
        <span style={{ color: colors.borderDefault }}>·</span>
        <span>
          {platformVersion != null
            ? `Last published: v${platformVersion} on ${formatDate(detail.updated_at)} by ${detail.updated_by ?? '—'}`
            : 'Never published'}
        </span>
        <span style={{ color: colors.borderDefault }}>·</span>
        <Tooltip title="Tenant usage available in Round 3">
          <Button
            size="small"
            startIcon={<GroupIcon sx={{ fontSize: 14 }} />}
            onClick={() =>
              setSnackbar({ open: true, msg: 'Tenant usage available in Round 3', sev: 'info' })
            }
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 600,
              color: 'inherit',
              p: 0,
              minWidth: 0,
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
            }}
          >
            Used by 0 tenants
          </Button>
        </Tooltip>
      </Box>

      {/* Inactive banner — non-sticky, editor disabled below it. */}
      {!detail.active && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: '8px' }}
          action={
            <WriteButton
              size="small"
              onClick={() => setReactivateOpen(true)}
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Reactivate
            </WriteButton>
          }
        >
          This task is inactive. It&apos;s hidden from tenants and cannot be edited.
        </Alert>
      )}

      {/* Unpublished changes banner */}
      {hasUnpublished && detail.active && (
        <Paper
          role="status"
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 4,
            mb: 3,
            p: 2,
            borderRadius: '8px',
            backgroundColor: colors.warningBg,
            borderLeft: `2px solid ${colors.warningIcon}`,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <WarningAmberIcon sx={{ color: colors.warningIcon, fontSize: 20 }} />
          <Typography sx={{ fontSize: 13, color: colors.warningText, flex: 1 }}>
            <strong>Unpublished changes.</strong> Tenants are still seeing v{platformVersion}.
            Publish this entity to roll it out.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <WriteButton
              variant="contained"
              size="small"
              sx={{
                ...primaryButtonSx,
                backgroundColor: colors.adminAccent,
                color: colors.bgWhite,
                '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
              }}
              onClick={() => setPublishOpen(true)}
            >
              Publish this entity
            </WriteButton>
            <WriteButton
              variant="outlined"
              size="small"
              sx={secondaryButtonSx}
              onClick={() => setDiscardOpen(true)}
            >
              Discard to v{platformVersion}
            </WriteButton>
          </Box>
        </Paper>
      )}

      {/* Version conflict inline banner */}
      {versionConflict != null && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: '8px' }}
          action={
            <Button size="small" onClick={handleReload}>
              Reload
            </Button>
          }
        >
          Someone else saved v{versionConflict} while you were editing.
        </Alert>
      )}

      {/* Form */}
      {form && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: `1px solid ${colors.borderLight}`,
            backgroundColor: colors.bgWhite,
            p: 3,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: 4,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            {/* Identity */}
            <Box id="identity">
              <SectionLabel label="Identity" hint="Code is immutable after creation." />
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  size="small"
                  label="task_code"
                  value={detail.task_code}
                  disabled
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <LockIcon sx={{ fontSize: 16, color: colors.textTertiary, mr: 1 }} />
                    ),
                    sx: { fontFamily: 'Fira Code, monospace', fontSize: 13 },
                  }}
                />
                <TextField
                  size="small"
                  label="task_name"
                  value={form.task_name}
                  onChange={(e) => setForm({ ...form, task_name: e.target.value })}
                  disabled={!editMode}
                  inputProps={{ maxLength: 80 }}
                  helperText={editMode ? `${form.task_name.length} / 80` : undefined}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch
                    checked={form.active}
                    disabled={!editMode}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  />
                  <Typography sx={{ fontSize: 13 }}>Active</Typography>
                </Box>
              </Box>
            </Box>

            {/* Classification */}
            <Box id="classification">
              <SectionLabel label="Classification" hint="FK references to reference catalogs." />
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  size="small"
                  select
                  SelectProps={{ native: true }}
                  label="Action type"
                  value={form.task_type_code}
                  disabled={!editMode || !!code}
                  onChange={(e) => setForm({ ...form, task_type_code: e.target.value })}
                  helperText={
                    types.length === 0 ? (
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <WarningAmberIcon sx={{ fontSize: 14, color: colors.warningIcon }} />
                        No task types —{' '}
                        <MuiLink
                          onClick={() => navigate(paths.admin.catalogs.taskTypes())}
                          sx={{ cursor: 'pointer' }}
                        >
                          create one →
                        </MuiLink>
                      </Box>
                    ) : undefined
                  }
                >
                  <option value="" disabled>
                    — Select —
                  </option>
                  {types.map((t) => (
                    <option key={t.code} value={t.code}>
                      {t.name} ({t.code})
                    </option>
                  ))}
                </TextField>

                <TextField
                  size="small"
                  select
                  SelectProps={{ native: true }}
                  label="task_category_code"
                  value={form.task_category_code}
                  disabled={!editMode}
                  onChange={(e) => setForm({ ...form, task_category_code: e.target.value })}
                  helperText={
                    cats.length === 0 ? (
                      <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <WarningAmberIcon sx={{ fontSize: 14, color: colors.warningIcon }} />
                        No task categories —{' '}
                        <MuiLink
                          onClick={() => navigate(paths.admin.catalogs.taskCategories())}
                          sx={{ cursor: 'pointer' }}
                        >
                          create one →
                        </MuiLink>
                      </Box>
                    ) : undefined
                  }
                >
                  <option value="" disabled>
                    — Select —
                  </option>
                  {cats.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </TextField>
              </Box>
            </Box>

            {/* Behavior */}
            <Box id="behavior" sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
              <SectionLabel label="Behavior" hint="How this task gets completed and reported." />
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {(
                  [
                    ['self_report', 'Self report'],
                    ['confirm_report', 'Confirm report'],
                    ['is_subtask', 'Is subtask'],
                  ] as [keyof FormState, string][]
                ).map(([key, label]) => (
                  <Box key={key as string} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch
                      checked={!!form[key]}
                      disabled={!editMode}
                      onChange={(e) =>
                        setForm({ ...form, [key]: e.target.checked } as FormState)
                      }
                    />
                    <Typography sx={{ fontSize: 13 }}>{label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Footer action bar */}
      {editMode && (
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            height: 64,
            zIndex: 10,
            backgroundColor: colors.bgWhite,
            borderTop: `1px solid ${colors.borderLight}`,
            display: 'flex',
            alignItems: 'center',
            px: 3,
            borderRadius: 0,
            gap: 1.5,
          }}
          role="toolbar"
          aria-label="Save actions"
        >
          <Button onClick={handleCancelEdit} sx={{ ...secondaryButtonSx, border: 'none' }}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          {isDirty && (
            <Typography sx={{ fontSize: 12, color: colors.textTertiary, mr: 1 }}>
              Unsaved changes
            </Typography>
          )}
          <WriteButton
            variant="outlined"
            onClick={() => saveMutation.mutate()}
            disabled={!isDirty || saveMutation.isPending}
            sx={secondaryButtonSx}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save draft'}{' '}
            <Box component="span" sx={{ fontSize: 11, opacity: 0.6, ml: 0.5 }}>
              (⌘S)
            </Box>
          </WriteButton>
          <WriteButton
            variant="contained"
            sx={{
              ...primaryButtonSx,
              backgroundColor: colors.adminAccent,
              color: colors.bgWhite,
              '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
            }}
            disabled={saveMutation.isPending}
            onClick={async () => {
              // If there's an unsaved draft, save first then open the
              // publish dialog. Otherwise go straight to publish.
              if (isDirty) {
                await saveMutation.mutateAsync().catch(() => null);
              }
              setPublishOpen(true);
            }}
          >
            Save &amp; Publish
          </WriteButton>
        </Paper>
      )}

      <TaskVersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        detail={detail}
      />

      {/* Publish — scoped to this single entity. */}
      <PublishConfirmationDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        entity="task"
        entities={[
          {
            entity: 'task',
            code: detail.task_code,
            task_code: detail.task_code,
            task_name: detail.task_name,
            current_version: detail.current_version,
            baseline_version: platformVersion,
            // R4 note: the server doesn't expose a per-field diff of
            // draft-vs-published yet. Dialog degrades to "No field-level
            // diff available" — acceptable for single-entity publish
            // since the user literally just saved the draft moments ago.
            changed_fields: [],
            diff: [],
          } as PublishEntity,
        ]}
        onCompleted={(res) => {
          setPublishOpen(false);
          if (res && res.batch_id) {
            setSnackbar({
              open: true,
              msg: `Published ${detail.task_code}`,
              sev: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', code] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'list'] });
          }
        }}
      />

      {/* Discard draft → roll back to last published version. */}
      {platformVersion != null && (
        <RollbackDialog
          open={discardOpen}
          onClose={() => setDiscardOpen(false)}
          taskCode={detail.task_code}
          targetVersion={platformVersion}
          currentVersion={detail.current_version}
          verb="Discard"
          onSuccess={(row) => {
            setDiscardOpen(false);
            setSnackbar({
              open: true,
              msg: `Discarded to v${platformVersion} (now v${row.current_version})`,
              sev: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', code] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'tasks', 'list'] });
          }}
        />
      )}

      {/* Reactivate confirm */}
      <Dialog
        open={reactivateOpen}
        onClose={() => !reactivateMutation.isPending && setReactivateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>
          Reactivate{' '}
          <Box component="code" sx={{ ...typo.mono, fontSize: 14 }}>
            {detail.task_code}
          </Box>
          ?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            It will become available to tenants on next sync.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setReactivateOpen(false)}
            sx={secondaryButtonSx}
            disabled={reactivateMutation.isPending}
          >
            Cancel
          </Button>
          <WriteButton
            variant="contained"
            onClick={() => reactivateMutation.mutate()}
            disabled={reactivateMutation.isPending}
            sx={{
              ...primaryButtonSx,
              backgroundColor: colors.adminAccent,
              color: colors.bgWhite,
              '&:hover': { backgroundColor: colors.adminAccent, opacity: 0.92 },
            }}
          >
            {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
          </WriteButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default TaskEdit;
